import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import './DynamicDB.css';
import './VentesDB.css';

// Bases de ventes VN / VO (snapshots SFTP, cf. backend/src/routes/ventes.js).
// Spécificités vs DynamicDB :
//   - pagination + recherche côté SERVEUR (20k lignes × 107 colonnes : on ne
//     charge jamais tout côté client) ;
//   - renommage des champs CRM-side via le panneau « ⚙️ Renommer les champs »
//     (super-admin) → PUT /columns-meta (label / groupe / visible / ordre /
//     rôle sémantique par colonne).

const GROUPES = ['Achats', 'Cessions', 'Dates', 'Général', 'Identité', 'Marge', 'Neutres', 'Ventes'];

// Rôles sémantiques exploitables plus tard par le Reporting financier (lit le
// rôle, jamais le code en dur — robuste aux changements de fichier).
const ROLES = [
  { value: '',          label: '—' },
  { value: 'ca_ht',     label: 'CA HT' },
  { value: 'ca_ttc',    label: 'CA TTC' },
  { value: 'marge',     label: 'Marge' },
  { value: 'pv_ttc',    label: 'Prix de vente TTC' },
  { value: 'pv_ht',     label: 'Prix de vente HT' },
  { value: 'remise',    label: 'Remise' },
  { value: 'cout',      label: "Coût / prix de revient" },
  { value: 'date_vente',label: 'Date de vente' },
  { value: 'vendeur',   label: 'Vendeur' },
];

// Pré-amorçage du groupe à partir du préfixe technique du code.
function inferGroupe(code) {
  if (/^S\d+$/.test(code))      return 'Ventes';
  if (/^A\d+$/.test(code))      return 'Achats';
  if (/^CI?\d+$/.test(code))    return 'Cessions';
  if (/^M(C|\d+)$/.test(code))  return 'Marge';
  if (/^N\d+$/.test(code))      return 'Neutres';
  return 'Général';
}

const LIMIT = 50;

export default function VentesDB({
  apiPath, title, user, concessions = [], syncSource = 'SFTP',
  manualUpload = false, filterField = null, filterOptions = null, filterLabel = 'Filtre',
  banner = null,
}) {
  const [entries, setEntries]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [columns, setColumns]   = useState([]);
  const [columnMeta, setColumnMeta] = useState({});
  const [pivotKey, setPivotKey] = useState(null);
  const [offset, setOffset]     = useState(0);
  const [search, setSearch]     = useState('');
  const [concFilter, setConcFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [loading, setLoading]   = useState(true);
  const [syncing, setSyncing]   = useState(false);
  const [importing, setImporting] = useState(false);
  const [toast, setToast]       = useState(null);
  const fileInputRef = useRef(null);

  const [showRename, setShowRename] = useState(false);
  const [draft, setDraft]       = useState({});
  const [savingMeta, setSavingMeta] = useState(false);

  const isSuperAdmin = user?.role === 'super-admin';
  const searchTimer = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: LIMIT, offset };
      if (search) params.q = search;
      if (concFilter) params.concession_id = concFilter;
      if (filterField && fieldFilter) { params.filter_key = filterField; params.filter_value = fieldFilter; }
      const res = await api.get(apiPath, { params });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      setColumns(res.data.columns || []);
      setColumnMeta(res.data.column_meta || {});
      setPivotKey(res.data.concessionPivotKey || null);
    } catch (err) {
      console.error(`Erreur chargement ${apiPath}:`, err);
      showToast(err.response?.data?.error || 'Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, [apiPath, offset, search, concFilter, filterField, fieldFilter]);

  useEffect(() => { load(); }, [load]);

  // Recherche : debounce + retour page 1.
  const onSearchChange = (v) => {
    setSearch(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setOffset(0), 400);
  };

  const runSync = async () => {
    try {
      setSyncing(true);
      const res = await api.post(`${apiPath}/sync`, {});
      const { imported } = res.data;
      setOffset(0);
      await load();
      showToast(`${imported} ligne${imported !== 1 ? 's' : ''} synchronisée${imported !== 1 ? 's' : ''} (snapshot complet)`);
    } catch (err) {
      showToast(err.response?.data?.error || `Erreur lors de la synchronisation ${syncSource}`, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      setImporting(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`${apiPath}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { imported, totalRows } = res.data;
      setOffset(0);
      await load();
      showToast(`${imported} ligne${imported !== 1 ? 's' : ''} importée${imported !== 1 ? 's' : ''} (${totalRows} ligne${totalRows !== 1 ? 's' : ''} dans le fichier)`);
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors de l'import du fichier", 'error');
    } finally {
      setImporting(false);
    }
  };

  // ── Renommage ──
  const openRename = () => {
    const d = {};
    columns.forEach((code, idx) => {
      const m = columnMeta[code] || {};
      d[code] = {
        label:  m.label || '',
        groupe: m.groupe || inferGroupe(code),
        visible: m.visible !== false,
        ordre:  m.ordre ?? idx,
        role:   m.role || '',
      };
    });
    setDraft(d);
    setShowRename(true);
  };

  const setField = (code, field, value) => {
    setDraft(prev => ({ ...prev, [code]: { ...prev[code], [field]: value } }));
  };

  const saveMeta = async () => {
    try {
      setSavingMeta(true);
      // On nettoie : on ne garde que les champs utiles, ordre numérique.
      const clean = {};
      Object.entries(draft).forEach(([code, m]) => {
        clean[code] = {
          label:  (m.label || '').trim(),
          groupe: m.groupe || 'Général',
          visible: m.visible !== false,
          ordre:  Number.isFinite(+m.ordre) ? +m.ordre : 0,
          role:   m.role || '',
        };
      });
      await api.put(`${apiPath}/columns-meta`, { column_meta: clean });
      setColumnMeta(clean);
      setShowRename(false);
      showToast('Noms de champs enregistrés');
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors de l'enregistrement", 'error');
    } finally {
      setSavingMeta(false);
    }
  };

  // Colonnes affichées : visibles, triées par (ordre ?? index d'origine).
  const displayCols = columns
    .map((code, idx) => ({ code, idx, m: columnMeta[code] || {} }))
    .filter(x => x.m.visible !== false)
    .sort((a, b) => (a.m.ordre ?? a.idx) - (b.m.ordre ?? b.idx));

  const colLabel = (code) => (columnMeta[code]?.label || code);
  const cell = (entry, code) => {
    const v = entry.data?.[code];
    return (v !== null && v !== undefined && v !== '') ? v : '-';
  };
  const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; } };

  const page = Math.floor(offset / LIMIT) + 1;
  const nbPages = Math.max(1, Math.ceil(total / LIMIT));
  const concList = (concessions || []).filter(c => c.id && c.nom);

  return (
    <div className="ddb-container">
      {toast && <div className={`ddb-toast ${toast.type}`}>{toast.msg}</div>}
      {banner && <div className="vdb-banner">{banner}</div>}

      {/* Toolbar */}
      <div className="ddb-toolbar">
        {manualUpload ? (
          <>
            <button className="btn-import-ddb" onClick={() => fileInputRef.current?.click()} disabled={importing}>
              {importing ? '⏳ Import en cours…' : '📥 Importer un fichier Excel'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </>
        ) : (
          <button className="btn-sync-ddb" onClick={runSync} disabled={syncing}>
            {syncing ? '⏳ Synchronisation…' : `🔄 Synchroniser depuis ${syncSource}`}
          </button>
        )}
        {isSuperAdmin && columns.length > 0 && (
          <button className="btn-import-ddb" onClick={openRename}>
            ⚙️ Renommer les champs
          </button>
        )}
        <input
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          className="ddb-search"
        />
        {search && <button className="btn-clear-ddb" onClick={() => { setSearch(''); setOffset(0); }}>Effacer</button>}
        {filterField && filterOptions && (
          <select
            className="ddb-search"
            value={fieldFilter}
            onChange={(e) => { setFieldFilter(e.target.value); setOffset(0); }}
            title={filterLabel}
          >
            <option value="">{filterLabel} : tous</option>
            {filterOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        {pivotKey && concList.length > 1 && (
          <select
            className="ddb-search"
            value={concFilter}
            onChange={(e) => { setConcFilter(e.target.value); setOffset(0); }}
            title="Filtrer par concession"
          >
            <option value="">Toutes concessions</option>
            {concList.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        )}
        <span className="ddb-count">{total} ligne{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="ddb-loading">Chargement...</div>
      ) : (
        <>
          <div className="ddb-table-wrapper">
            <table className="ddb-table">
              <thead>
                <tr>
                  {pivotKey && <th>Concession</th>}
                  {displayCols.map(({ code }) => (
                    <th key={code} title={`Code source : ${code}`}>{colLabel(code)}</th>
                  ))}
                  <th>Date import</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={displayCols.length + (pivotKey ? 1 : 0) + 1} className="ddb-empty">
                      {total === 0
                        ? (manualUpload
                            ? 'Aucune donnée. Importez un fichier Excel pour commencer.'
                            : `Aucune donnée. Lancez une synchronisation ${syncSource} pour commencer.`)
                        : 'Aucun résultat pour cette recherche.'}
                    </td>
                  </tr>
                ) : entries.map(entry => {
                  const rawPivot = pivotKey ? (entry.data?.[pivotKey] ?? '') : '';
                  return (
                    <tr key={entry.id}>
                      {pivotKey && (
                        <td title={rawPivot ? `Code source : ${rawPivot}` : undefined}>
                          {entry.concession_nom
                            ? entry.concession_nom
                            : <span className="ddb-conc-unmapped">{rawPivot || '—'}</span>}
                        </td>
                      )}
                      {displayCols.map(({ code }) => <td key={code}>{cell(entry, code)}</td>)}
                      <td className="ddb-date">{fmtDate(entry.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > LIMIT && (
            <div className="vdb-pager">
              <button disabled={offset === 0} onClick={() => setOffset(0)}>« Début</button>
              <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>‹ Précédent</button>
              <span>Page {page} / {nbPages}</span>
              <button disabled={page >= nbPages} onClick={() => setOffset(offset + LIMIT)}>Suivant ›</button>
              <button disabled={page >= nbPages} onClick={() => setOffset((nbPages - 1) * LIMIT)}>Fin »</button>
            </div>
          )}
        </>
      )}

      {/* ── Panneau renommage ── */}
      {showRename && (
        <div className="import-choice-overlay" onClick={() => setShowRename(false)}>
          <div className="vdb-rename-modal" onClick={e => e.stopPropagation()}>
            <div className="import-choice-header">
              <h3>⚙️ Renommer les champs — {title}</h3>
              <button className="import-choice-close" onClick={() => setShowRename(false)}>×</button>
            </div>
            <div className="vdb-rename-body">
              <p className="vdb-rename-intro">
                Donnez un nom métier à chaque colonne (ex. <code>S11</code> → « CA HT »). Le code source reste affiché en gris.
                Décochez « Visible » pour masquer une colonne du tableau. Le <strong>rôle</strong> sert au futur Reporting (il référence le rôle, pas le code).
              </p>
              <table className="vdb-rename-table">
                <thead>
                  <tr>
                    <th>Code</th><th>Nom métier</th><th>Groupe</th><th>Visible</th><th>Ordre</th><th>Rôle</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map(code => {
                    const d = draft[code] || {};
                    return (
                      <tr key={code}>
                        <td><code>{code}</code></td>
                        <td>
                          <input
                            type="text"
                            value={d.label || ''}
                            placeholder={code}
                            onChange={e => setField(code, 'label', e.target.value)}
                          />
                        </td>
                        <td>
                          <select value={d.groupe || 'Général'} onChange={e => setField(code, 'groupe', e.target.value)}>
                            {GROUPES.map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={d.visible !== false} onChange={e => setField(code, 'visible', e.target.checked)} />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="vdb-ordre"
                            value={d.ordre ?? 0}
                            onChange={e => setField(code, 'ordre', e.target.value)}
                          />
                        </td>
                        <td>
                          <select value={d.role || ''} onChange={e => setField(code, 'role', e.target.value)}>
                            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="ddb-sync-actions">
              <button className="btn-secondary-ddb" onClick={() => setShowRename(false)}>Annuler</button>
              <button className="btn-primary-ddb" onClick={saveMeta} disabled={savingMeta}>
                {savingMeta ? 'Enregistrement…' : '✅ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
